/**
 * shareExport.js
 * ---------------
 * Shared utilities for the unified share menu. Three core capabilities:
 *
 *   1. exportNodeToPng(node)  → blob+dataUrl of a beautiful 1080×1080-ish card
 *   2. exportNodeToPdf(node, { multiPage })  → blob+dataUrl A4 PDF
 *      • When `multiPage` is true the node is sliced into A4-height pages
 *        (used for long lessons).
 *   3. shareOrDownload(blob, filename, { fallbackText })
 *      • Uses Web Share API on mobile when available
 *      • Falls back to download via <a download>
 *      • Falls back to text copy if both image & PDF fail upstream
 *
 * RTL / Hebrew font handling
 *   We deliberately rasterise to PNG before stuffing into the PDF —
 *   jsPDF's native text engine doesn't ship Hebrew fonts and shaping
 *   RTL is fragile. PNG-in-PDF avoids that entirely. Trade-off: slightly
 *   larger file size, no copy-pasteable text. Acceptable for share/print.
 *
 * Fallback chain (used by ShareMenu)
 *   PNG fails → try PDF → copy text → toast "נסה שוב"
 */
import { toPng, toBlob } from "html-to-image";

/**
 * Render a DOM node into a PNG. Returns { blob, dataUrl }.
 * Throws on failure — caller is expected to catch and fall back.
 */
export async function exportNodeToPng(node, { pixelRatio = 2 } = {}) {
  if (!node) throw new Error("share-export: missing node");
  // Two-pass render: html-to-image occasionally drops webfonts on the
  // first paint (Safari, mostly). The second pass gives webfonts a
  // chance to load and produces a reliable image.
  await toPng(node, { pixelRatio: 1, cacheBust: true });
  const blob = await toBlob(node, { pixelRatio, cacheBust: true });
  if (!blob) throw new Error("share-export: empty blob");
  const dataUrl = await blobToDataUrl(blob);
  return { blob, dataUrl };
}

/**
 * Render a DOM node into a PDF and return { blob, dataUrl }.
 *
 *   • `multiPage: false` (default) — captures the node as a single PNG
 *     and fits it onto a single A4 page (portrait or square depending on
 *     aspect ratio). Best for verse / parasha / aggadah cards.
 *
 *   • `multiPage: true` — captures the full node at native height and
 *     slices it across multiple A4 pages. Used for long lessons.
 *
 * The node is rendered off-screen at high resolution. Caller is
 * responsible for mounting and unmounting the print-only DOM target.
 */
export async function exportNodeToPdf(node, { multiPage = false, filename = "torahya.pdf" } = {}) {
  if (!node) throw new Error("share-export: missing node");
  // Defer-load jsPDF — keeps the main bundle slim. The library lives
  // in /app/frontend/node_modules/jspdf and exports `jsPDF` as a named
  // export when imported via ESM.
  const { jsPDF } = await import("jspdf");
  const { blob } = await exportNodeToPng(node, { pixelRatio: 2 });
  const imgDataUrl = await blobToDataUrl(blob);
  const img = await loadImage(imgDataUrl);

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWmm = pdf.internal.pageSize.getWidth();   // 210
  const pageHmm = pdf.internal.pageSize.getHeight();  // 297
  const marginMm = 10;
  const usableWmm = pageWmm - marginMm * 2;
  const usableHmm = pageHmm - marginMm * 2;

  // px→mm ratio at the node's actual rendered resolution
  const pxPerMm = img.width / usableWmm;

  if (!multiPage) {
    // Single-page fit
    const scaledHmm = img.height / pxPerMm;
    const fitsOnOne = scaledHmm <= usableHmm;
    if (fitsOnOne) {
      pdf.addImage(imgDataUrl, "PNG", marginMm, marginMm, usableWmm, scaledHmm, undefined, "FAST");
    } else {
      // If a "single-page" card overflows, fall through to the slicing path
      // so we never silently crop content.
      sliceIntoPages(pdf, img, imgDataUrl, { marginMm, usableWmm, usableHmm, pxPerMm });
    }
  } else {
    sliceIntoPages(pdf, img, imgDataUrl, { marginMm, usableWmm, usableHmm, pxPerMm });
  }

  const out = pdf.output("blob");
  const dataUrl = await blobToDataUrl(out);
  return { blob: out, dataUrl, filename };
}

/**
 * Slice a tall image across as many A4 pages as needed. Implemented by
 * drawing the full image with a negative Y offset on each successive
 * page and using a clipping rectangle so only the current slice is
 * visible. This is the canonical jsPDF + html-to-image pattern.
 */
function sliceIntoPages(pdf, img, imgDataUrl, { marginMm, usableWmm, usableHmm, pxPerMm }) {
  const sliceHmm = usableHmm;            // mm consumed per page
  const slicePx = sliceHmm * pxPerMm;     // px consumed per page
  let renderedPx = 0;
  let page = 0;
  while (renderedPx < img.height) {
    if (page > 0) pdf.addPage();
    const remainingPx = img.height - renderedPx;
    const thisSlicePx = Math.min(slicePx, remainingPx);
    const thisSliceHmm = thisSlicePx / pxPerMm;
    // Trick: addImage doesn't support clipping; instead we let the
    // image extend off the page and rely on jsPDF's automatic
    // clipping at the page edge. Setting Y so that the slice we
    // care about lands inside the margins.
    pdf.addImage(
      imgDataUrl, "PNG",
      marginMm,
      marginMm - (renderedPx / pxPerMm),
      usableWmm,
      img.height / pxPerMm,
      undefined, "FAST"
    );
    // Mask the area above + below the current slice so the previous
    // and next slices don't bleed into the page. We paint white
    // rectangles over them.
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), marginMm, "F"); // top
    pdf.rect(0, marginMm + thisSliceHmm, pdf.internal.pageSize.getWidth(),
             pdf.internal.pageSize.getHeight(), "F"); // bottom + footer area
    renderedPx += thisSlicePx;
    page += 1;
    if (page > 30) break; // hard safety cap
  }
}

/**
 * Native share when available, otherwise download. Returns a string
 * describing which path was taken — useful for analytics + tests.
 */
export async function shareOrDownload(blob, filename, { title, text } = {}) {
  if (!blob) throw new Error("share-export: missing blob");
  const file = new File([blob], filename, { type: blob.type });
  if (
    typeof navigator !== "undefined" &&
    navigator.canShare &&
    navigator.canShare({ files: [file] }) &&
    typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({ files: [file], title, text });
      return "shared";
    } catch (e) {
      // user cancelled — surface a sentinel so the caller doesn't
      // accidentally treat it as a failure
      if (e?.name === "AbortError") return "cancelled";
      // any other error → fall through to download
    }
  }
  downloadBlob(blob, filename);
  return "downloaded";
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyTextToClipboard(text) {
  if (!text) throw new Error("share-export: empty text");
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  // Legacy fallback: hidden textarea
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  ta.remove();
  if (!ok) throw new Error("share-export: copy failed");
  return true;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
}

/**
 * Helper — generate the universal share URL for an entry. We always
 * surface torahya.com (the canonical domain) regardless of which
 * environment the user is on; share recipients should always land on
 * the production site.
 */
export function buildShareUrl(path = "/") {
  // If we're on a torahya host already use the current origin so the
  // referral query string survives. Otherwise force the production
  // domain — preview URLs don't make sense to share.
  const isCanonical =
    typeof window !== "undefined" &&
    /torahya\.com$/i.test(window.location.hostname);
  const origin = isCanonical ? window.location.origin : "https://torahya.com";
  return `${origin}${path}`;
}
